import { useCallback, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import {
	type AdControlInteractedCommonAttributes,
	type AdControlScreenNames,
	type ViewedScreenCommonAttributes,
} from '@atlassian/surface-analytics-specs';

import { SURFACE_ANALYTICS_PRODUCT_MAP } from '../../ccp-products';
import { CROSS_FLOW_CHANNEL, eventTypes } from '../../constants';
import { type RecommendationProductIds } from '../../types';
import { type AdControlInteractedUIEvents } from '../types';

export const useFireAdControlAnalyticsEvents = (): {
	fireInteractedUIEvent: <Attributes extends Record<string, any>>(
		event: AdControlInteractedUIEvents,
		commonAttributes: AdControlInteractedCommonAttributes,
		attributes?: Attributes,
	) => void;
	fireViewedScreenEvent: <Attributes extends Record<string, any>>(
		name: AdControlScreenNames,
		commonAttributes: ViewedScreenCommonAttributes,
		attributes?: Attributes,
	) => void;
} => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const firedViewedScreenEventName = useRef<string>();

	const mapToCcpProductKeys = useCallback(
		(recommendedProductIds: string[], targetProductId?: string): RecommendationProductIds => {
			const ccpProductIds = recommendedProductIds.reduce((acc, curr) => {
				acc.push(SURFACE_ANALYTICS_PRODUCT_MAP.products[curr] ?? curr);
				return acc;
			}, [] as string[]);

			const ccpTargetProductId =
				targetProductId && SURFACE_ANALYTICS_PRODUCT_MAP.products[targetProductId];

			return {
				recommendedProductIds: ccpProductIds,
				targetProductId: ccpTargetProductId ?? targetProductId,
			};
		},
		[],
	);

	const fireViewedScreenEvent = useCallback(
		<Attributes extends Record<string, any>>(
			name: AdControlScreenNames,
			commonAttributes: ViewedScreenCommonAttributes,
			attributes?: Attributes,
		): void => {
			// Prevent duplicate events from firing
			if (firedViewedScreenEventName.current === name) {
				return;
			}

			const { recommendedProductIds, targetProductId, ...rest } = commonAttributes;

			const { recommendedProductIds: ccpProductIds, targetProductId: ccpTargetProductId } =
				mapToCcpProductKeys(recommendedProductIds, targetProductId);

			createAnalyticsEvent({
				eventType: eventTypes.SCREEN,
				name,
				attributes: {
					recommendedProductIds: ccpProductIds,
					targetProductId: ccpTargetProductId,
					...rest,
					...attributes,
				},
			}).fire(CROSS_FLOW_CHANNEL);

			firedViewedScreenEventName.current = name;
		},

		[createAnalyticsEvent, mapToCcpProductKeys],
	);

	const fireInteractedUIEvent = useCallback(
		<Attributes extends Record<string, any>>(
			event: AdControlInteractedUIEvents,
			commonAttributes: AdControlInteractedCommonAttributes,
			attributes?: Attributes,
		): void => {
			const { source, recommendedProductIds, targetProductId, ...rest } = commonAttributes;

			const { recommendedProductIds: ccpProductIds, targetProductId: ccpTargetProductId } =
				mapToCcpProductKeys(recommendedProductIds ?? [], targetProductId);

			createAnalyticsEvent({
				eventType: eventTypes.UI,
				...event,
				source,
				attributes: {
					recommendedProductIds: ccpProductIds,
					targetProductId: ccpTargetProductId,
					...rest,
					...attributes,
				},
			}).fire(CROSS_FLOW_CHANNEL);
		},
		[createAnalyticsEvent, mapToCcpProductKeys],
	);

	return {
		fireViewedScreenEvent,
		fireInteractedUIEvent,
	};
};
